import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import ParkingPermit from "@/components/Guest/ParkingPermit.tsx";
import { useGuestLoginMutation, useGuestVehicleMutation } from "@/mutations/guest.ts";
import GuestLoginForm from "@/routes/guest/-forms/GuestLoginForm.tsx";
import ParkingPermitForm from "@/routes/guest/-forms/ParkingPermitForm.tsx";

export const Route = createFileRoute("/guest/")({
    component: RouteComponent,
});

const loginSchema = z.object({
    customerName: z.string().min(1, "Last name is required"),
    rentalId: z.string().min(1, "Unit number is required"),
});
export type LoginFormData = z.infer<typeof loginSchema>;

const parkingSchema = z
    .object({
        carMaker: z.string().optional(),
        carLicense: z.string().optional(),
        noCarCheckbox: z.boolean().optional(),
    })
    .refine(
        (data) => {
            if (!data.noCarCheckbox) {
                return !!data.carMaker;
            }
            return true;
        },
        {
            message: "Car make is required unless you check you will not have a car.",
            path: ["carMaker"],
        },
    )
    .refine(
        (data) => {
            if (!data.noCarCheckbox) {
                return !!data.carLicense;
            }
            return true;
        },
        {
            message: "Car licence is required unless you check you won't have a car.",
            path: ["carLicense"],
        },
    );
export type ParkingFormData = z.infer<typeof parkingSchema>;

function RouteComponent() {
    const [parkingInfo, setParkingInfo] = useState<ParkingFormData | null>(null);
    const loginForm = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            customerName: "",
            rentalId: "",
        },
    });
    const guestLoginMutation = useGuestLoginMutation({
        setError: loginForm.setError,
    });

    const parkingForm = useForm<ParkingFormData>({
        resolver: zodResolver(parkingSchema),
        defaultValues: {
            carMaker: guestLoginMutation.data?.carMaker ?? "",
            carLicense: guestLoginMutation.data?.carLicense ?? "",
            noCarCheckbox: false,
        },
    });
    const guestVehicleMutation = useGuestVehicleMutation({
        setError: parkingForm.setError,
    });

    const handleLoginSubmit = useCallback(
        (data: LoginFormData) => {
            guestLoginMutation.mutate({
                attributes: data,
                type: "reservation",
            });
        },
        [guestLoginMutation.mutate],
    );

    const handleParkingSubmit = useCallback(
        (data: ParkingFormData) => {
            if (!guestLoginMutation.data) {
                guestLoginMutation.reset();
                return;
            }
            setParkingInfo(data);
            guestVehicleMutation.mutate({
                type: "reservation",
                attributes: {
                    ...data,
                    carLicense: data.carLicense ? data.carLicense.toUpperCase() : undefined,
                    customerName: guestLoginMutation.data.customerName,
                    rentalId: guestLoginMutation.data.rentalId,
                },
            });
        },
        [guestLoginMutation.data, guestLoginMutation.reset, guestVehicleMutation.mutate],
    );

    if (!guestLoginMutation.data) {
        return <GuestLoginForm onSubmit={handleLoginSubmit} form={loginForm} />;
    }

    if (!(guestVehicleMutation.data && parkingInfo)) {
        return (
            <ParkingPermitForm
                onSubmit={handleParkingSubmit}
                form={parkingForm}
                reservation={guestLoginMutation.data}
            />
        );
    }

    return (
        <ParkingPermit
            parkingInfo={parkingInfo}
            reservation={guestLoginMutation.data}
            gateCodes={guestVehicleMutation.data.data}
            reset={() => {
                setTimeout(() => {
                    setParkingInfo(null);
                    guestVehicleMutation.reset();
                    guestLoginMutation.reset();
                    loginForm.reset();
                    window.location.href = "http://checkin.mahanaaoao.com/";
                }, 30_000);
            }}
        />
    );
}
